import React, { useEffect, useState } from 'react';
import { MapPin, AlertCircle, Loader2 } from 'lucide-react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';

interface LocationInputProps {
  label: string;
  placeholder: string;
  value?: string;
  onLocationSelect: (location: { address: string; coordinates: [number, number]; placeId?: string }) => void;
  icon?: React.ReactNode;
  className?: string;
}

export default function LocationInput({ 
  label, 
  placeholder, 
  value, 
  onLocationSelect, 
  icon,
  className = ""
}: LocationInputProps) {
  const { isLoaded, error: mapsError } = useGoogleMapsLoader();
  const [isSelecting, setIsSelecting] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  
  // Initialiser use-places-autocomplete seulement quand Google Maps est chargé
  const {
    ready,
    value: placesValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: { 
      componentRestrictions: { country: 'fr' },
      language: 'fr'
    },
    debounce: 300,
    defaultValue: value,
    initOnMount: isLoaded // N'initialise que si Google Maps est chargé
  });

  useEffect(() => {
    if (value && !isSelecting) {
      setValue(value, false);
    }
  }, [value, setValue, isSelecting]);

  const handleSelect = async (address: string, placeId?: string) => {
    setIsSelecting(true);
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      const resultPlaceId = results[0]?.place_id || placeId;
      
      onLocationSelect({
        address,
        coordinates: [lng, lat],
        placeId: resultPlaceId
      });
    } catch (error) {
    } finally {
      setIsSelecting(false);
    }
  };

  // Monitor Google Maps loading status
  useEffect(() => {
    if (mapsError) {
      console.error('Google Maps API error:', mapsError);
    }
  }, [mapsError]);

  if (mapsError) {
    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
        <div className="flex items-center p-3 text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg backdrop-blur-sm">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span className="text-sm">Erreur de chargement de l'API Google Maps</span>
        </div>
      </div>
    );
  }

  if (!isLoaded || !ready) {
    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon || <MapPin className="h-5 w-5 text-yellow-500" />}
          </div>
          <div className={`animate-pulse bg-black/30 border border-gray-800 h-12 rounded-lg pl-10 flex items-center ${className}`}>
            <Loader2 className="w-4 h-4 text-yellow-500 animate-spin mr-2" />
            <span className="text-gray-400 text-sm">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
          {icon || <MapPin className="h-5 w-5 text-yellow-500" />}
        </div>
        <input
          type="text"
          value={placesValue || inputValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            setValue(newValue);
          }}
          placeholder={placeholder}
          className={`w-full px-4 py-3 pl-12 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent backdrop-blur-sm ${className}`}
        />
        {isSelecting && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />
          </div>
        )}
        {ready && status === "OK" && data.length > 0 && (
          <div className="absolute z-50 mt-1 w-full bg-[#1E1E1E]/95 backdrop-blur-xl border border-gray-800 rounded-lg shadow-2xl max-h-60 overflow-auto">
            {data.map((suggestion, index) => {
              const {
                place_id,
                structured_formatting: { main_text, secondary_text },
              } = suggestion;

              return (
                <div
                  key={place_id}
                  onClick={() => handleSelect(suggestion.description, place_id)}
                  className={`cursor-pointer select-none relative px-4 py-3 hover:bg-yellow-500/10 transition-colors ${
                    index === 0 ? 'rounded-t-lg' : ''
                  } ${
                    index === data.length - 1 ? 'rounded-b-lg' : 'border-b border-gray-800/50'
                  }`}
                >
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 text-yellow-500 mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{main_text}</div>
                      {secondary_text && (
                        <div className="text-gray-400 text-sm truncate">{secondary_text}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}